import { useState, useMemo, useEffect, useRef } from 'react';

export const useSalesFilters = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('today');
  const [customDateRange, setCustomDateRange] = useState(null);
  const [creatorFilter, setCreatorFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [page, setPage] = useState(1);
  const prevFiltersRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, statusFilter, dateFilter, creatorFilter, customerFilter, customDateRange]);

  // Memoized filters object for API calls - only update when filter values actually change
  const apiFilters = useMemo(() => {
    const filters = {
      page,
      page_size: 25
    };
    
    // Search parameter (use debounced version)
    if (debouncedSearchTerm.trim()) {
      filters.search = debouncedSearchTerm.trim();
    }
    
    // Status parameter - map to uppercase as API expects
    if (statusFilter && statusFilter !== 'all') {
      filters.status = statusFilter.toUpperCase();
    }
    
    // Creator parameter - use created_by ID
    if (creatorFilter && creatorFilter !== 'all') {
      filters.creator = creatorFilter;
    }
    
    // Customer parameter
    if (customerFilter && customerFilter !== 'all') {
      filters.customer = customerFilter;
    }
    
    // Date filter can be handled on the backend or converted to date ranges
    if (dateFilter && dateFilter !== 'all') {
      if (dateFilter === 'custom' && customDateRange) {
        filters.filter_date = 'custom_date';
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        filters.start_date = formatDate(customDateRange.startDate);
        filters.end_date = formatDate(customDateRange.endDate);
      } else {
        const today = new Date();
        let startDate, endDate;
        filters.filter_date = dateFilter;
        
        // switch (dateFilter) {
        //   case 'today':
        //     startDate = new Date(today);
        //     startDate.setHours(0, 0, 0, 0);
        //     endDate = new Date(today);
        //     endDate.setHours(23, 59, 59, 999);
        //     break;
        //   case 'yesterday':
        //     startDate = new Date(today);
        //     startDate.setDate(today.getDate() - 1);
        //     startDate.setHours(0, 0, 0, 0);
        //     endDate = new Date(today);
        //     endDate.setDate(today.getDate() - 1);
        //     endDate.setHours(23, 59, 59, 999);
        //     break;
        //   case 'last_7_days':
        //     startDate = new Date(today);
        //     startDate.setDate(today.getDate() - 7);
        //     startDate.setHours(0, 0, 0, 0);
        //     endDate = new Date(today);
        //     endDate.setHours(23, 59, 59, 999);
        //     break;
        //   case 'this_month':
        //     startDate = new Date(today);
        //     startDate.setDate(1);
        //     startDate.setHours(0, 0, 0, 0);
        //     endDate = new Date(today);
        //     endDate.setHours(23, 59, 59, 999);
        //     break;
        //   default:
        //     break;
        // }
        
        if (startDate && endDate) {
          const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };
          filters.start_date = formatDate(startDate);
          filters.end_date = formatDate(endDate);
        }
      }
    }
    
    // Compare with previous filters to maintain reference stability
    const filtersString = JSON.stringify(filters);
    const prevFiltersString = prevFiltersRef.current ? JSON.stringify(prevFiltersRef.current) : '';
    
    // Only return new object if filters actually changed
    if (prevFiltersString !== filtersString) {
      prevFiltersRef.current = filters;
      return filters;
    }
    
    // Return previous filters object to maintain reference stability
    return prevFiltersRef.current || filters;
  }, [debouncedSearchTerm, statusFilter, dateFilter, creatorFilter, customerFilter, page]);

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('all');
    setDateFilter('today');
    setCustomDateRange(null);
    setCreatorFilter('all');
    setCustomerFilter('all');
    setPage(1);
  };

  return {
    // Filter state
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    dateFilter,
    setDateFilter,
    customDateRange,
    setCustomDateRange,
    creatorFilter,
    setCreatorFilter,
    customerFilter,
    setCustomerFilter,
    page,
    setPage,
    
    // API filters
    apiFilters,
    
    // Actions
    clearFilters
  };
};